import { ArrowDownToLine, RefreshCw, Search, TrendingUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  intervalLimit,
  intradayHistoryPageLimit,
  marketApi,
  subscribeToMarket,
} from "./api";
import { MarketChart } from "./components/MarketChart";
import { OptionChain } from "./components/OptionChain";
import { OptionMiniChart } from "./components/OptionMiniChart";
import { mergeChronological } from "./history";
import {
  dashboardPermalinkHref,
  readDashboardPermalink,
  type DashboardPermalink,
  type PermalinkOptionSelection,
} from "./permalink";
import {
  findPermalinkOption,
  permalinkSelectionForOption,
  reconcileSelectedOption,
} from "./optionSelection";
import {
  chainStatusLabel,
  chainStatusTone,
  formatMarketTimestamp,
} from "./feedStatus";
import {
  eventRefreshesOptionBars,
  eventRefreshesStockBars,
} from "./marketEvents";
import { StatusStrip } from "./components/StatusStrip";
import {
  formatCompact,
  formatPercent,
  formatPrice,
  signedClass,
} from "./format";
import type {
  Instrument,
  MacdResponse,
  MarketEvent,
  OptionChainResponse,
  OptionContract,
  OptionGreekPoint,
  OptionInterval,
  PriceBar,
  ReadinessResponse,
  StockBarsResponse,
  StockInterval,
  StockSnapshot,
} from "./types";

const FALLBACK_INSTRUMENTS: Instrument[] = ["META", "GOOGL", "MSFT"].map(
  (symbol) => ({
    symbol,
    name: symbol,
    exchange: "NASDAQ",
    currency: "USD",
    last_price: null,
    change: null,
    percent_change: null,
    quote_time: null,
  }),
);
const WATCHLIST_SYMBOLS = FALLBACK_INSTRUMENTS.map(
  (instrument) => instrument.symbol,
);

const INTERVALS: { value: StockInterval; label: string }[] = [
  { value: "1m", label: "1m" },
  { value: "5m", label: "5m" },
  { value: "30m", label: "30m" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];

function savedInterval(): StockInterval {
  const value = window.localStorage.getItem("xiaoming-stock-interval");
  return INTERVALS.some((item) => item.value === value)
    ? (value as StockInterval)
    : "daily";
}

function optionSelectionFromPermalink(
  permalink: DashboardPermalink,
): PermalinkOptionSelection | null {
  return permalink.optionType && permalink.strikePriceCents !== null
    ? {
        optionType: permalink.optionType,
        strikePriceCents: permalink.strikePriceCents,
      }
    : null;
}

export default function App() {
  const initialPermalink = useRef(
    readDashboardPermalink(window.location.search, WATCHLIST_SYMBOLS),
  ).current;
  const [instruments, setInstruments] =
    useState<Instrument[]>(FALLBACK_INSTRUMENTS);
  const [symbol, setSymbol] = useState(initialPermalink.symbol ?? "META");
  const [interval, setInterval] = useState<StockInterval>(savedInterval);
  const [snapshot, setSnapshot] = useState<StockSnapshot | null>(null);
  const [stockBars, setStockBars] = useState<StockBarsResponse | null>(null);
  const [macd, setMacd] = useState<MacdResponse | null>(null);
  const [expirations, setExpirations] = useState<string[]>([]);
  const [expiration, setExpiration] = useState(
    initialPermalink.expiration ?? "",
  );
  const [permalinkOption, setPermalinkOption] =
    useState<PermalinkOptionSelection | null>(
      optionSelectionFromPermalink(initialPermalink),
    );
  const [chain, setChain] = useState<OptionChainResponse | null>(null);
  const [selectedOption, setSelectedOption] = useState<OptionContract | null>(
    null,
  );
  const [optionInterval, setOptionInterval] =
    useState<OptionInterval>("5m");
  const [optionBars, setOptionBars] = useState<PriceBar[]>([]);
  const [optionGreeks, setOptionGreeks] = useState<OptionGreekPoint[]>([]);
  const [optionNativeInterval, setOptionNativeInterval] = useState<
    boolean | null
  >(null);
  const [stockLoading, setStockLoading] = useState(true);
  const [chainLoading, setChainLoading] = useState(true);
  const [optionLoading, setOptionLoading] = useState(false);
  const [stockHistoryLoading, setStockHistoryLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [readiness, setReadiness] = useState<ReadinessResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const optionChainRef = useRef<HTMLElement | null>(null);
  const lastOptionSectionScrollRef = useRef<string | null>(null);
  const stockHistoryLoadingRef = useRef(false);
  const symbolRef = useRef(symbol);
  const expirationRef = useRef(expiration);
  const expirationsRef = useRef(expirations);
  symbolRef.current = symbol;
  expirationRef.current = expiration;
  expirationsRef.current = expirations;

  const currentInstrument = useMemo(
    () => instruments.find((item) => item.symbol === symbol),
    [instruments, symbol],
  );

  const loadReadiness = useCallback(async () => {
    try {
      setReadiness(await marketApi.readiness());
    } catch {
      setReadiness((current) => ({
        status: "unavailable",
        database: current?.database,
        feeds: {
          ...current?.feeds,
          equities: {
            ...current?.feeds?.equities,
            status: "unavailable",
            detail: "Readiness check unavailable",
          },
          options: {
            ...current?.feeds?.options,
            status: "unavailable",
            detail: "Readiness check unavailable",
          },
          historical: {
            ...current?.feeds?.historical,
            status: "unavailable",
            detail: "Readiness check unavailable",
          },
          analytics: {
            ...current?.feeds?.analytics,
            status: "unavailable",
            detail: "Readiness check unavailable",
          },
        },
      }));
    }
  }, []);

  const loadInstruments = useCallback(async () => {
    try {
      const response = await marketApi.instruments();
      if (response.instruments.length) setInstruments(response.instruments);
    } catch {
      // The fixed watchlist remains usable while the first cache seed runs.
    }
  }, []);

  const loadStock = useCallback(async () => {
    setStockLoading(true);
    const limit = intervalLimit(interval);
    const [snapshotResult, barsResult, macdResult] = await Promise.allSettled([
      marketApi.snapshot(symbol),
      marketApi.bars(symbol, interval, limit),
      marketApi.macd(symbol, interval, limit),
    ]);
    if (snapshotResult.status === "fulfilled") setSnapshot(snapshotResult.value);
    if (barsResult.status === "fulfilled") {
      const response = barsResult.value;
      setStockBars((current) =>
        current?.symbol === response.symbol &&
        current.interval === response.interval
          ? {
              ...response,
              has_more: current.has_more,
              next_before: current.next_before,
              bars: mergeChronological(
                current.bars,
                response.bars,
                (bar) => bar.open_time,
              ),
            }
          : response,
      );
      setError(null);
    } else {
      setError("Stock history is warming up");
    }
    if (macdResult.status === "fulfilled") {
      const response = macdResult.value;
      setMacd((current) =>
        current?.symbol === response.symbol &&
        current.interval === response.interval
          ? {
              ...response,
              has_more: current.has_more,
              next_before: current.next_before,
              points: mergeChronological(
                current.points,
                response.points,
                (point) => point.observed_at,
              ),
            }
          : response,
      );
    }
    setStockLoading(false);
  }, [interval, symbol]);

  const loadOlderStockHistory = useCallback(async () => {
    const before = stockBars?.next_before;
    const pageLimit = intradayHistoryPageLimit(interval);
    if (
      !before ||
      !stockBars.has_more ||
      pageLimit === 0 ||
      stockHistoryLoadingRef.current
    ) {
      return;
    }

    const requestSymbol = symbol;
    const requestInterval = interval;
    stockHistoryLoadingRef.current = true;
    setStockHistoryLoading(true);
    try {
      const [barsResponse, macdResponse] = await Promise.all([
        marketApi.bars(requestSymbol, requestInterval, pageLimit, before),
        marketApi.macd(requestSymbol, requestInterval, pageLimit, before),
      ]);
      setStockBars((current) => {
        if (
          current?.symbol !== requestSymbol ||
          current.interval !== requestInterval
        ) {
          return current;
        }
        return {
          ...current,
          has_more: barsResponse.has_more,
          next_before: barsResponse.next_before,
          bars: mergeChronological(
            current.bars,
            barsResponse.bars,
            (bar) => bar.open_time,
          ),
        };
      });
      setMacd((current) => {
        if (
          current?.symbol !== requestSymbol ||
          current.interval !== requestInterval
        ) {
          return current;
        }
        return {
          ...current,
          has_more: macdResponse.has_more,
          next_before: macdResponse.next_before,
          points: mergeChronological(
            current.points,
            macdResponse.points,
            (point) => point.observed_at,
          ),
        };
      });
    } catch {
      setError("Earlier stock history could not be loaded");
    } finally {
      stockHistoryLoadingRef.current = false;
      setStockHistoryLoading(false);
    }
  }, [interval, stockBars, symbol]);

  const loadExpirations = useCallback(async () => {
    const requestSymbol = symbol;
    try {
      const response = await marketApi.expirations(symbol);
      if (symbolRef.current !== requestSymbol) return;
      setExpirations(response.expirations);
      setExpiration((current) =>
        response.expirations.includes(current)
          ? current
          : (response.expirations[0] ?? ""),
      );
    } catch {
      if (symbolRef.current !== requestSymbol) return;
      setExpirations([]);
      setExpiration("");
    }
  }, [symbol]);

  const loadChain = useCallback(async (silent = false) => {
    if (!expiration || !expirations.includes(expiration)) {
      setChain(null);
      if (!silent) setChainLoading(Boolean(expiration));
      return;
    }
    const requestSymbol = symbol;
    const requestExpiration = expiration;
    const isCurrentRequest = () =>
      symbolRef.current === requestSymbol &&
      expirationRef.current === requestExpiration;

    if (!silent) setChainLoading(true);
    try {
      const response = await marketApi.chain(
        requestSymbol,
        requestExpiration,
      );
      if (!isCurrentRequest()) return;
      setChain(response);
      setError(null);
    } catch {
      if (!isCurrentRequest()) return;
      if (!silent) {
        setChain(null);
        setError("Option chain is warming up");
      }
    } finally {
      if (!silent && isCurrentRequest()) setChainLoading(false);
    }
  }, [expiration, expirations, symbol]);

  const loadOptionBars = useCallback(async () => {
    if (!selectedOption) return;
    setOptionLoading(true);
    setOptionNativeInterval(null);
    try {
      const response = await marketApi.optionBars(
        symbol,
        selectedOption.code,
        optionInterval,
      );
      setOptionBars(response.bars);
      setOptionGreeks(response.greeks);
      setOptionNativeInterval(response.native_interval);
    } catch {
      setOptionBars([]);
      setOptionGreeks([]);
      setOptionNativeInterval(null);
    } finally {
      setOptionLoading(false);
    }
  }, [optionInterval, selectedOption, symbol]);

  useEffect(() => {
    void loadInstruments();
  }, [loadInstruments]);

  useEffect(() => {
    void loadReadiness();
    const timer = window.setInterval(() => void loadReadiness(), 5_000);
    return () => window.clearInterval(timer);
  }, [loadReadiness]);

  useEffect(() => {
    window.localStorage.setItem("xiaoming-stock-interval", interval);
    void loadStock();
  }, [interval, loadStock]);

  useEffect(() => {
    void loadExpirations();
  }, [loadExpirations]);

  useEffect(() => {
    setSelectedOption(null);
    setOptionBars([]);
    setOptionGreeks([]);
  }, [expiration, symbol]);

  useEffect(() => {
    void loadChain();
  }, [loadChain]);

  useEffect(() => {
    if (
      !chain ||
      chain.symbol !== symbol ||
      chain.expiration !== expiration
    ) {
      return;
    }
    if (permalinkOption) {
      const restored = findPermalinkOption(
        chain.contracts,
        permalinkOption,
      );
      setSelectedOption(restored);
      if (!restored) setPermalinkOption(null);
      return;
    }
    setSelectedOption((current) =>
      reconcileSelectedOption(current, chain.contracts),
    );
  }, [chain, expiration, permalinkOption, symbol]);

  useEffect(() => {
    const selectedCode = selectedOption?.code ?? null;
    if (!permalinkOption || !selectedCode) {
      if (!permalinkOption) lastOptionSectionScrollRef.current = null;
      return;
    }
    if (lastOptionSectionScrollRef.current === selectedCode) return;

    const frame = window.requestAnimationFrame(() => {
      optionChainRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      lastOptionSectionScrollRef.current = selectedCode;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [permalinkOption, selectedOption?.code]);

  useEffect(() => {
    void loadOptionBars();
  }, [loadOptionBars]);

  useEffect(() => {
    const handleEvent = (event: MarketEvent) => {
      if (event.symbol !== symbol) {
        if (event.type === "stock_snapshot") void loadInstruments();
        return;
      }
      if (event.type === "stock_snapshot") {
        void loadStock();
        void loadInstruments();
      } else if (eventRefreshesStockBars(event, interval)) {
        void loadStock();
      } else if (
        event.type === "option_chain" &&
        event.expiration === expiration
      ) {
        void loadChain(true);
        if (selectedOption?.code) {
          void loadOptionBars();
        }
      } else if (
        eventRefreshesOptionBars(
          event,
          selectedOption?.code ?? null,
          optionInterval,
        )
      ) {
        void loadOptionBars();
      }
    };
    return subscribeToMarket(handleEvent, setConnected);
  }, [
    expiration,
    interval,
    loadChain,
    loadInstruments,
    loadOptionBars,
    loadStock,
    optionInterval,
    selectedOption?.code,
    symbol,
  ]);

  useEffect(() => {
    const refresh = () => {
      void loadInstruments();
      void loadExpirations();
      void loadChain(true);
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") void loadChain(true);
    };
    const timer = window.setInterval(refresh, 60_000);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [loadChain, loadExpirations, loadInstruments]);

  useEffect(() => {
    const href = dashboardPermalinkHref(window.location.href, {
      symbol,
      expiration: expiration || null,
      optionType: permalinkOption?.optionType ?? null,
      strikePriceCents: permalinkOption?.strikePriceCents ?? null,
    });
    window.history.replaceState(null, "", href);
  }, [expiration, permalinkOption, symbol]);

  useEffect(() => {
    const restorePermalink = () => {
      const selection = readDashboardPermalink(
        window.location.search,
        WATCHLIST_SYMBOLS,
      );
      const nextSymbol = selection.symbol ?? "META";
      setPermalinkOption(optionSelectionFromPermalink(selection));
      setSelectedOption(null);
      setOptionBars([]);
      setOptionGreeks([]);
      if (nextSymbol !== symbolRef.current) {
        setSymbol(nextSymbol);
        setExpiration(selection.expiration ?? "");
        setExpirations([]);
        setSnapshot(null);
        setStockBars(null);
        setMacd(null);
        setStockHistoryLoading(false);
        setChain(null);
        return;
      }

      const available = expirationsRef.current;
      setExpiration(
        selection.expiration && available.includes(selection.expiration)
          ? selection.expiration
          : (available[0] ?? selection.expiration ?? ""),
      );
    };
    window.addEventListener("popstate", restorePermalink);
    return () => window.removeEventListener("popstate", restorePermalink);
  }, []);

  const selectSymbol = (nextSymbol: string) => {
    if (nextSymbol === symbol) return;
    window.history.pushState(
      null,
      "",
      dashboardPermalinkHref(window.location.href, {
        symbol: nextSymbol,
      }),
    );
    setPermalinkOption(null);
    setSymbol(nextSymbol);
    setExpiration("");
    setExpirations([]);
    setSnapshot(null);
    setStockBars(null);
    setMacd(null);
    setStockHistoryLoading(false);
    setChain(null);
    setSelectedOption(null);
    setOptionBars([]);
    setOptionGreeks([]);
  };

  const selectExpiration = (nextExpiration: string) => {
    if (nextExpiration === expiration) return;
    window.history.pushState(
      null,
      "",
      dashboardPermalinkHref(window.location.href, {
        symbol,
        expiration: nextExpiration,
      }),
    );
    setPermalinkOption(null);
    setExpiration(nextExpiration);
    setSelectedOption(null);
    setOptionBars([]);
    setOptionGreeks([]);
  };

  const selectStockInterval = (nextInterval: StockInterval) => {
    if (nextInterval === interval) return;
    setInterval(nextInterval);
    setStockBars(null);
    setMacd(null);
    setStockHistoryLoading(false);
  };

  const selectOption = (contract: OptionContract) => {
    if (selectedOption?.code === contract.code) return;
    const nextPermalinkOption = permalinkSelectionForOption(contract);
    window.history.pushState(
      null,
      "",
      dashboardPermalinkHref(window.location.href, {
        symbol,
        expiration,
        optionType: nextPermalinkOption.optionType,
        strikePriceCents: nextPermalinkOption.strikePriceCents,
      }),
    );
    setPermalinkOption(nextPermalinkOption);
    setSelectedOption(contract);
    setOptionBars([]);
    setOptionGreeks([]);
    void marketApi.selectOption(symbol, {
      expiration,
      contract: contract.code,
    });
  };

  const closeOption = () => {
    if (selectedOption || permalinkOption) {
      window.history.pushState(
        null,
        "",
        dashboardPermalinkHref(window.location.href, {
          symbol,
          expiration,
        }),
      );
    }
    setPermalinkOption(null);
    setSelectedOption(null);
    setOptionBars([]);
    setOptionGreeks([]);
  };

  const refreshAll = () => {
    void loadReadiness();
    void loadInstruments();
    void loadStock();
    void loadExpirations();
    void loadChain();
    void loadOptionBars();
  };

  const jumpToOptionChain = () => {
    optionChainRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const lastPrice = snapshot?.last_price ?? currentInstrument?.last_price ?? null;
  const change = snapshot?.change ?? currentInstrument?.change ?? null;
  const percentChange =
    snapshot?.percent_change ?? currentInstrument?.percent_change ?? null;
  return (
    <main className="app-shell">
      <header className="app-header">
        <a className="brand" href="/" aria-label="Xiaoming Stocks home">
          <span className="brand-mark">
            <TrendingUp size={19} />
          </span>
          <span>
            <strong>XIAOMING</strong>
            <small>STOCKS</small>
          </span>
        </a>
        <nav className="symbol-tabs" aria-label="Watchlist">
          {instruments.map((instrument) => (
            <button
              type="button"
              key={instrument.symbol}
              className={instrument.symbol === symbol ? "active" : ""}
              onClick={() => selectSymbol(instrument.symbol)}
            >
              <strong>{instrument.symbol}</strong>
              <span>{formatPrice(instrument.last_price)}</span>
              <em className={signedClass(instrument.percent_change)}>
                {formatPercent(instrument.percent_change)}
              </em>
            </button>
          ))}
        </nav>
        <div className="header-actions">
          <button
            type="button"
            className="header-jump-button"
            onClick={jumpToOptionChain}
            aria-label="Jump to Option Chain"
            title="Jump to Option Chain"
          >
            <ArrowDownToLine size={15} />
            <span>Jump to Option Chain</span>
          </button>
          <button
            type="button"
            className="icon-button"
            onClick={refreshAll}
            aria-label="Refresh market data"
            title="Refresh market data"
          >
            <RefreshCw size={17} />
          </button>
        </div>
      </header>

      <StatusStrip
        connected={connected}
        lastUpdateTime={snapshot?.quote_time ?? currentInstrument?.quote_time ?? null}
        lastTradeTime={snapshot?.trade_time ?? null}
        provider={snapshot?.source ?? null}
        macdVersion={macd?.algorithm_version ?? null}
        readiness={readiness}
      />

      {error && (
        <div className="cache-notice" role="status">
          {error}
        </div>
      )}

      <section className="stock-workspace" aria-labelledby="stock-heading">
        <div className="stock-toolbar">
          <div className="symbol-quote">
            <div>
              <h1 id="stock-heading">{symbol}</h1>
              <span>{currentInstrument?.name ?? symbol}</span>
            </div>
            <strong className={signedClass(change)}>{formatPrice(lastPrice)}</strong>
            <div className={`quote-change ${signedClass(change)}`}>
              <span>{change && change > 0 ? "+" : ""}{formatPrice(change)}</span>
              <span>{formatPercent(percentChange)}</span>
            </div>
            <dl className="quote-stats">
              <div>
                <dt>OPEN</dt>
                <dd>{formatPrice(snapshot?.open)}</dd>
              </div>
              <div>
                <dt>HIGH</dt>
                <dd>{formatPrice(snapshot?.high)}</dd>
              </div>
              <div>
                <dt>LOW</dt>
                <dd>{formatPrice(snapshot?.low)}</dd>
              </div>
              <div>
                <dt>VOLUME</dt>
                <dd>{formatCompact(snapshot?.volume)}</dd>
              </div>
              <div>
                <dt>BID / ASK</dt>
                <dd>
                  {formatPrice(snapshot?.bid)} / {formatPrice(snapshot?.ask)}
                </dd>
              </div>
            </dl>
          </div>
          <div className="stock-intervals" role="tablist" aria-label="Stock interval">
            {INTERVALS.map((item) => (
              <button
                key={item.value}
                type="button"
                role="tab"
                aria-selected={interval === item.value}
                className={interval === item.value ? "active" : ""}
                onClick={() => selectStockInterval(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <MarketChart
          key={`${symbol}:${interval}`}
          bars={stockBars?.bars ?? []}
          indicators={macd?.points ?? []}
          loading={stockLoading}
          historyLoading={stockHistoryLoading}
          hasMoreHistory={stockBars?.has_more ?? false}
          onLoadOlder={loadOlderStockHistory}
        />
      </section>

      <section
        ref={optionChainRef}
        className="options-workspace"
        aria-labelledby="options-heading"
      >
        <div className="options-toolbar">
          <div className="options-title">
            <Search size={16} />
            <h2 id="options-heading">{symbol} OPTION CHAIN</h2>
            <span>{chain?.contracts.length ?? 0} CONTRACTS</span>
          </div>
          <label className="expiration-select">
            <span>EXPIRATION</span>
            <select
              value={expiration}
              onChange={(event) => selectExpiration(event.target.value)}
              disabled={expirations.length === 0}
            >
              {expirations.length === 0 && <option value="">Loading…</option>}
              {expirations.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <div
            className={`chain-quote-time ${chainStatusTone(chain?.status)}`}
            title={chain?.status_detail ?? undefined}
          >
            CHAIN {chainStatusLabel(chain?.status)} · LAST QUOTE{" "}
            {formatMarketTimestamp(
              chain?.databento_quote_time ?? chain?.quote_time,
            )}
          </div>
        </div>
        <div className="options-content">
          <OptionChain
            contracts={chain?.contracts ?? []}
            underlyingPrice={lastPrice}
            selectedCode={selectedOption?.code ?? null}
            loading={chainLoading}
            onSelect={selectOption}
          />
          <OptionMiniChart
            contract={selectedOption}
            interval={optionInterval}
            bars={optionBars}
            greeks={optionGreeks}
            nativeInterval={optionNativeInterval}
            loading={optionLoading}
            onIntervalChange={setOptionInterval}
            onClose={closeOption}
          />
        </div>
      </section>
    </main>
  );
}
